<template>
	<section class="PanelAccounting accounting ignore-deal-lock">
		<IsBusySectionComponent
			v-if="!!isBusyText"
			:text="getIsBusyText"
			delayToShowText="100"
		/>

		<!--IF WE ALREADY POSTED THIS DEAL, WE SHOW THE TRANSACTIONS THAT WERE POSTED -->
		<template v-else-if="hasAccountingFinished">
			<h3>Accounting Month: {{ getMonthAndYear() }}</h3>
			<AccountingTransactionsRichTable
				:getServerSide="loadTransactions"
				:onTransactionsSelected="handleTransactionSelectionChange"
				:isSelectable="true"
				:store="fimenu.store"
				:fimenu="fimenu"
				:postingCallback="init"
				:usePanelTitle="true"
				:charterOfAccounts
				:journalInfo
			/>
		</template>

		<!--IF WE DID NOT POST THE DEAL YET, WE SHOW THE ACCOUNTNG ITEMS THAT WOULD BE POSTED -->
		<div
			v-else
			class="accounting-container"
			id="accountingContainer"
			:key="hasUnmappedAmount ? 'all-mapped' : 'has-unmapped'"
		>
			<div class="container-header">
				<div class="section-title">
					<h3 :style="{ margin: 0 }">
						{{ getHeaderMessage }}
					</h3>
					<span
						class="subtitle"
						:key="dealsAccountingMonthEnum"
						>Deal's Accounting Month: {{ monthsEnumsHelper.getDisplay(dealsAccountingMonthEnum) }}</span
					>
				</div>
				<div :style="{ display: 'flex', gap: '10px' }">
					<InputRichDropdown
						v-model:saturn="selectedDealStatus"
						:list="filteredDealStatuses()"
						:valueMap="fimenu.dealStatusEnumsHelper.getToListItemValue"
						:display="fimenu.dealStatusEnumsHelper.getPosition"
						label="Send Deal To"
					/>
					<button
						@click="confirmStatusChange"
						:disabled="!selectedDealStatus"
					>
						Confirm
					</button>
				</div>
				<fieldset
					v-if="isDealOnOrPassedAccounting"
					:style="{ display: 'flex', gap: '10px' }"
				>
					<!-- BUTTON TO RE-MAP THE ACCOUNTING VALUES -->
					<button @click="() => init(true)">Re-Map Accounting Values</button>

					<!-- BUTTON TO MAP THE ACCOUNTING VALUES WITH MOCK DATA -->
					<button
						v-if="$global.isAdminView && hasUnmappedAmount"
						class="button-action"
						@click="mapWithFakeData()"
						title="Transactions mapped with fake data won't be posted."
					>
						Map With Fake Data
					</button>
				</fieldset>
			</div>

			<div class="content-helper-container">
				<InputCheckbox
					v-model="showOtherAmounts"
					label="Show Accounting Items With Zero/Ignored Values"
				/>
				<input
					v-model="searchText"
					placeholder="Search"
					class="search-input"
					@input="handleSearchQuery"
				/>
			</div>

			<template v-if="categories.length">
				<AccordionList
					:list="categories"
					:closeOtherOnOpen="!hasSearchText"
					:defaultExpanded="hasSearchText"
					:listLabel="(c: any) => c.category.display"
					ref="accordionList"
					@change="resetAllPageSizes"
				>
					<template #listHeader="{ headerItem: row }">
						<button
							v-if="hasUnmappedInCategory(row) && !isIgnoredList(row.category)"
							class="button-unwind hint-click"
							@click="goToTheNextErrorInTheCategory(row)"
						>
							{{ getUnmappedInCategory(row).length }}
						</button>
					</template>

					<template #listBody="{ bodyItem: row, index: categoryIndex }">
						<div
							class="category-details"
							:ref="categoryIndex as any"
						>
							<Panel
								style="height: 100%; margin-top: 15px"
								title="Accounting Items"
								v-if="hasValidTransactionsForCategory(row)"
							>
								<RichTable
									:headers="accountingNumbersHeader"
									:tableData="validTransactionsForCategory(row)"
									class="smaller-text"
									@row-click="openEditAccountingModal"
								>
									<template #name="transaction">
										<div class="name-cell-content">
											{{ formatPath(transaction.name) }}
										</div>
									</template>

									<template #accountNumber="transaction">
										<div
											class="account-number"
											:id="getErrorElementId(transaction)"
										>
											<span v-if="transaction.accountNumber">{{ transaction.accountNumber }}</span>
											<span v-else>
												<i class="fas fa-exclamation-circle" />
											</span>
										</div>
									</template>

									<template #amount="transaction">
										<div class="amount-container">
											<span>{{ formatAmount(transaction.amount) }}</span>
										</div>
									</template>
								</RichTable>
							</Panel>

							<div
								v-else-if="searchText"
								class="warning-container warning-unmapped"
							>
								<span class="blink">No valid accounting items match your search.</span>
							</div>

							<div
								v-else
								class="warning-container warning-unmapped"
							>
								<span class="blink">Not Mapped</span>
							</div>

							<div
								class="auto-scroller"
								v-seen="(observer: IntersectionObserver) => showMoreItemsForCategory(row, observer)"
							></div>
						</div>

						<div
							v-if="showOtherAmounts"
							class="hidden-items-container"
						>
							<Panel
								style="height: 100%; margin-top: 15px"
								title="Zero Amounts"
								v-if="zeroAmountTransactionsForCategory(row.category).length > 0"
							>
								<RichTable
									:headers="zeroValuesTableHeaders"
									:tableData="zeroAmountTransactionsForCategory(row.category)"
								>
									<template #amount="row">
										<div class="amount-container">
											<span>{{ formatAmount(row.amount) }}</span>
										</div>
									</template>
								</RichTable>
							</Panel>
							<Panel
								style="height: 100%; margin-top: 15px"
								title="Ignored Amounts"
								v-if="ignoredTransactionsForCategory(row.category).length > 0"
							>
								<RichTable
									:headers="zeroValuesTableHeaders"
									:tableData="ignoredTransactionsForCategory(row.category)"
									@row-click="openEditAccountingModal"
								>
									<template #amount="transaction">
										<div class="amount-container">
											<span>{{ formatAmount(transaction.amount) }}</span>
										</div>
									</template>
								</RichTable>
							</Panel>
						</div>
					</template>
				</AccordionList>
			</template>
			<template v-else-if="hasSearchText">
				<h2 class="not-found-title">
					No accounting items found under "<span class="searchText">{{ searchText }}</span
					>"
				</h2>
			</template>
			<template v-else>
				<h2 class="not-found-title">No accounting items found.</h2>
			</template>
		</div>

		<template v-if="!isBusyText">
			<div :class="feedbackClassName">
				<span
					class="blink"
					v-if="!hasAccountingFinished && hasUnmappedAmount"
				>
					{{ hoveredMenuFeedbackMessage }}
				</span>
			</div>
			<fieldset
				class="button-container"
				:disabled="!isDealOnOrPassedAccounting"
			>
				<button
					v-if="isAccountingInProcess"
					:class="{
						'posting-btn hint-click': true,
						'button-unwind': hasAccountingFinished && !hasSelectedTransactions,
					}"
					@click="openPostingModal"
					:disabled="!canPostOrReverseTransactions"
				>
					{{ !hasAccountingFinished || hasSelectedTransactions ? 'Post' : 'Reverse' }}
				</button>
				<button
					@click="finalizeDeal"
					:class="{
						'posting-btn': true,
						'button-save': !hasAccountingFinished,
						'button-delete': hasAccountingFinished,
					}"
				>
					{{ hasAccountingFinished ? 'Unwind' : 'Finalize' }} Deal
				</button>
			</fieldset>
		</template>
	</section>
</template>
<script lang="ts">
	export interface IPanelAccountingProps {
		fimenu: FIMenu;
	}
	export interface ICategoryTableRow {
		pageSize: number;
		category: ToListItems<AccountingValueCategory>;
	}
</script>
<script setup lang="ts">
	//#region imports
	import { AccountingValueCategory, DealStatuses, AccountingTransactionTypes, Month, ProfitFlags, FIMenuSection } from '@core/classes/SharedEnums';
	import { computed, onBeforeMount, onMounted, onUnmounted, Ref, ref, useTemplateRef, nextTick } from 'vue';
	import { DYWAccountSubTypeDisplay, DYWAccountType, DYWAccountTypeDisplay } from '@core/_enums/DataYourWay/DYWAccountingEnums';

	import AccountingHelper, { DealAmountHeader } from '@/helpers/AccountingHelper';
	import $modal from '@core/services/modal';
	import AccordionList from '@core/components/AccordionList.vue';
	import { accountingAPI } from '@core/services/api/api.accounting';
	import AccountingTransaction from '@core/classes/Accounting/AccountingTransaction';
	import AccountingTransactionsRichTable from './AccountingTransactionsRichTable.vue';
	import auth from '@core/services/auth';
	import ChecklistHelper from '@/helpers/checklist-helper';
	import { DYWCharterOfAccounts } from '@core/classes/DataYourWay/Accounting/DYWCharterOfAccounts';
	import { DYWJournalInfo } from '@core/classes/DataYourWay/Accounting/DYWJournalInfo';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuAccountingItems from '@core/classes/Accounting/FIMenuAccountingItems';
	import { getRandomEnumValue } from '@core/classes/UtilClasses';
	import GlobalAccountingAmount from '@core/classes/Accounting/GlobalAccountingAmount';
	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import IsBusySectionComponent from '@core/components/IsBusySectionComponent.vue';
	import modalEditAccountingItem from '@/modals/modalEditAccountingItem.vue';
	import moment from 'moment';
	import MonthEnumsHelper from '@core/_enums/MonthEnumsHelper';
	import Panel from '@core/components/Panel.vue';
	import RichTable from '@core/components/RichTable.vue';
	import { RichTableServerSide } from '@core/classes/RichTableServerSide';
	import StoreAccountingAmount from '@core/classes/Accounting/StoreAccountingAmount';
	import { ToListItems } from '@core/helpers/enum-helper';
	import util from '@core/services/util';
	//#endregion

	//#region states
	const { fimenu } = defineProps<IPanelAccountingProps>();
	const currentStoreAccountingMonth = moment(fimenu.store.storeSettings.accountingSettings.currentAccountingMonth, 'YYYY-M-D').startOf('month');
	const accordionListRef = useTemplateRef('accordionList');

	const accountingHelper = new AccountingHelper();
	const selectedDealStatus = ref<DealStatuses>(null) as Ref<DealStatuses>;
	const charterOfAccounts = ref<DYWCharterOfAccounts[]>([]) as Ref<DYWCharterOfAccounts[]>;
	const journalInfo = ref<DYWJournalInfo[]>([]) as Ref<DYWJournalInfo[]>;
	const DEFAULT_PAGE_SIZE = 10;
	// Initialized so that we don't mount transactions table twice, which doubles the amount of API calls
	const isBusyText = ref<string>('Retrieving Accounting Data...');
	const isUserReadyToFinalize = ref(false);
	const showOtherAmounts = ref(false);
	const monthsEnumsHelper = new MonthEnumsHelper();

	const categories = ref<ICategoryTableRow[]>([]) as Ref<ICategoryTableRow[]>;
	const searchText = ref<string>(null);
	const selectedTransactionsForPosting = ref<AccountingTransaction[]>([]) as Ref<AccountingTransaction[]>;
	let dealAccountingItems: FIMenuAccountingItems[] = [];
	let accountingItemsForStore: StoreAccountingAmount[] = [];
	let globalAccountingItems: GlobalAccountingAmount[] = [];
	const accountingNumbersHeader = ref<DealAmountHeader[]>([
		{
			name: 'name',
			display: 'Name',
			value: (transaction: AccountingTransaction) => formatPath(transaction.name),
			sortable: true,
			cssClass: 'name-cell',
			slot: 'name',
		},
		{
			name: 'accountNumber',
			display: 'Acct. Number',
			sortable: true,
			slot: 'accountNumber',
			cssClass: 'smaller-cell',
		},
		{
			name: 'accountType',
			display: 'Acct. Type',
			value: (transaction: AccountingTransaction) => DYWAccountTypeDisplay[transaction.accountType],
			sortable: true,
			cssClass: 'smaller-cell',
		},
		{
			name: 'accountSubType',
			display: 'Acct. Sub Type',
			value: (transaction: AccountingTransaction) => DYWAccountSubTypeDisplay[transaction.accountSubType],
			sortable: true,
			cssClass: 'smaller-cell',
		},
		{
			name: 'profitFlag',
			display: 'Profit Flag',
			value: (transaction: AccountingTransaction) => accountingHelper.profitFlagsEnums.getDisplay(transaction.profitFlag),
		},
		{
			name: 'company',
			display: 'Company',
			cssClass: 'smaller-cell',
		},
		{
			name: 'control1',
			display: 'Control 1',
			cssClass: '',
		},
		{
			name: 'control2',
			display: 'Control 2',
			cssClass: '',
		},
		{
			name: 'description',
			display: 'Description',
			cssClass: '',
		},
		{
			name: 'amount',
			display: 'Amount',
			slot: 'amount',
			cssClass: '',
		},
	]);
	const zeroValuesTableHeaders = ref<DealAmountHeader[]>([
		{
			name: 'name',
			display: 'Name',
			value: (transaction: AccountingTransaction) => formatPath(transaction.name),
			sortable: true,
			cssClass: '',
		},
		{
			name: 'amount',
			display: 'Amount',
			slot: 'amount',
			cssClass: 'wide-column-75',
		},
	]);
	//#endregion

	//#region computed
	/**
	 * Returns the payload of the current user
	 * @returns {any}
	 */
	const payload = computed(() => auth.getTokenPayload());

	/**
	 * Returns true if the transactions can be posted or reversed
	 * @returns {boolean}
	 */
	const canPostOrReverseTransactions = computed((): boolean => {
		// IF THE DEAL IS NOT IN THE SAME ACCOUNTING MONTH AS THE STORE, RETURN FALSE
		if (!hasAccountingFinished.value && !isDealInTheSameAccountingMonthAsStore.value) return false;

		// IF THE DEAL IS NOT FINISHED, RETURN TRUE IF THERE ARE NO UNMAPPED AMOUNTS
		if (!hasAccountingFinished.value) return !hasUnmappedAmount.value;
		// IF THE DEAL IS FINISHED, RETURN TRUE IF THE SELECTED TRANSACTIONS ARE IN THE SAME ACCOUNTING MONTH AS THE STORE
		else {
			if (hasSelectedTransactions.value) {
				// IF THERE ARE SELECTED TRANSACTIONS, CHECK IF THEY ARE IN THE SAME ACCOUNTING MONTH AS THE STORE
				return selectedTransactionsForPosting.value.every(i => moment(i.postingMonth).isSame(currentStoreAccountingMonth, 'month'));
			}
			// IF THERE ARE NO SELECTED TRANSACTIONS, RETURN TRUE TO ALLOW REVERSING OF THE DEAL
			return true;
		}
	});

	/**
	 * Returns the deal accounting month enum
	 * @returns {Month}
	 */
	const dealsAccountingMonthEnum = computed((): Month => {
		return moment(fimenu.accountingInfo.accountingMonth).month();
	});

	/**
	 * Returns true if the deal is in the same accounting month as the store
	 * @returns {boolean}
	 */
	const isDealInTheSameAccountingMonthAsStore = computed((): boolean => {
		return moment(fimenu.accountingInfo.accountingMonth).isSame(currentStoreAccountingMonth, 'month');
	});

	/**
	 * Returns true if the deal has unmapped amounts
	 * @returns {boolean}
	 */
	const hasUnmappedAmount = computed((): boolean => {
		// only map the valid transactions. Use the categories list to get all the valid transactions for each category
		return categories.value.some(row => hasUnmappedInCategory(row));
	});

	/**
	 * Returns true if the deal is in accounting
	 * @returns {boolean}
	 */
	const isDealOnOrPassedAccounting = computed((): boolean => {
		return fimenu.dealStatusEnumsHelper.isOnOrPassedStatus(fimenu.dealStatus, DealStatuses.AccountingInProcess);
	});

	/**
	 * Returns true if there is an open accordion
	 * @returns {boolean}
	 */
	const hasAnOpenAccordion = computed((): boolean => {
		if (!accordionListRef.value) return false;

		//use the ref to see if any are expanded
		return accordionListRef.value.selectedAccordion !== null;
	});

	/**
	 * Returns the feedback class name
	 * @returns {string}
	 */
	const feedbackClassName = computed((): string => {
		let className = 'feedback';

		if (!hasAnOpenAccordion.value && hasUnmappedAmount.value && !hasSearchText.value) return className + ' expanded errors-found';

		if (isUserReadyToFinalize.value && !hasSearchText.value) className += ' expanded';

		return className;
	});

	/**
	 * Returns the header message
	 * @returns {string}
	 */
	const getHeaderMessage = computed((): string => {
		//Status Change Header Messages
		if (searchText.value) return 'Searching through the deal values...';
		else if (hasUnmappedAmount.value) return 'Costing Clerk work pending';
		else if (isPostReady.value) return 'Ready For Posting!';
		else return 'Deal Is Posted and Finalized';
	});

	/**
	 * Returns the hovered menu feedback message
	 * @returns {string}
	 */
	const hoveredMenuFeedbackMessage = computed((): string => {
		const unmappedCount = getTotalUnmapped();

		return `You have ${unmappedCount} unmapped item${unmappedCount === 1 ? '' : 's'}. To post, map all the remaining items.`;
	});

	/**
	 * Returns true if there is search text
	 * @returns {boolean}
	 */
	const hasSearchText = computed((): boolean => {
		return searchText.value && searchText.value.length >= 3;
	});

	/**
	 * Returns true if the accounting is finished
	 * @returns {boolean}
	 */
	const hasAccountingFinished = computed((): boolean => {
		return fimenu.dealStatusEnumsHelper.isOnOrPassedStatus(fimenu.dealStatus, DealStatuses.AccountingFinished);
	});

	/**
	 * Returns true if the deal is ready to post
	 * @returns {boolean}
	 */
	const isPostReady = computed((): boolean => {
		return (
			!fimenu.dealStatusEnumsHelper.isOnOrPassedStatus(fimenu.dealStatus, DealStatuses.AccountingFinished) &&
			!hasUnmappedAmount.value &&
			isDealOnOrPassedAccounting.value
		);
	});

	/**
	 * Returns the busy text
	 * @returns {string}
	 */
	const getIsBusyText = computed((): string => {
		return isBusyText.value ?? 'Please wait while we do some accounting...';
	});

	/**
	 * Returns the deal accounting transactions
	 * @returns {AccountingTransaction[]}
	 */
	const dealAccountingTransactions = computed((): AccountingTransaction[] => {
		return fimenu.accountingValues;
	});

	/**
	 * Returns true if there are selected transactions
	 * @returns {boolean}
	 */
	const hasSelectedTransactions = computed((): boolean => {
		return !!selectedTransactionsForPosting.value.length;
	});

	/**
	 * Returns true if the deal is posting
	 * @returns {boolean}
	 */
	const isPosting = computed(() => {
		const isPostingAdjustableOrChargeback = selectedTransactionsForPosting.value.some(
			i => i.transactionType === AccountingTransactionTypes.Adjustment || i.transactionType === AccountingTransactionTypes.Chargeback,
		);
		return !hasAccountingFinished.value || isPostingAdjustableOrChargeback;
	});

	/**
	 * Returns true if the deal is in accounting
	 * @returns {boolean}
	 */
	const isAccountingInProcess = computed((): boolean => {
		return fimenu.dealStatusEnumsHelper.isInAccounting(fimenu.dealStatus) && !fimenu.isAccountingFinalized();
	});
	//#endregion

	//#region lifecycle hooks
	onBeforeMount(() => {});

	onMounted(async () => {
		// Always load charter of accounts and journals, regardless of posting status
		const apiCalls = [loadCharterOfAccountsAndJournals()];

		if (!fimenu.accountingInfo.isPosted) {
			apiCalls.push(init());
		}

		await Promise.all(apiCalls);
	});

	onUnmounted(() => {
		if (!fimenu.isAccountingFinalized()) {
			fimenu.clearAccountingValues();
		}
	});
	//#endregion

	//#region methods

	/**
	 * Initializes the accounting values
	 * @param {boolean} reevaluate - Whether to reevaluate the accounting values
	 */
	async function init(reevaluate = false) {
		try {
			isBusyText.value = 'Retrieving Accounting Data...';
			searchText.value = null;
			if (!dealAccountingTransactions.value.length || reevaluate) {
				const [dealAccountingTransactionsResponse, dealAccountingItemsResponse, storeAccountingItemsResponse] = await Promise.all([
					//WE RETRIEVE ALL DEAL ACCOUNTING TRANSACTIONS
					fimenu.getAccountingValues(),

					//WE RETRIEVE ALL DEAL ACCOUNTING ITEMS (THIS IS NECESSARY IF WE WANT TO EDIT THE AMOUNT OF AN ITEM)
					accountingHelper.getAccountingItems(fimenu),

					//WE RETRIEVE ALL STORE ACCOUNTING ITEMS (THIS IS NECESSARY IF WE WANT TO EDIT THE AMOUNT OF AN ITEM)
					accountingAPI.getStoreAccountingSettings(fimenu.store.storeCode),
				]);

				//WE RETRIEVE ALL DEAL ACCOUNTING ITEMS (THIS IS NECESSARY IF WE WANT TO EDIT THE AMOUNT OF AN ITEM)
				dealAccountingItems = dealAccountingItemsResponse;

				//WE RETRIEVE ALL STORE ACCOUNTING ITEMS (THIS IS NECESSARY IF WE WANT TO EDIT THE AMOUNT OF AN ITEM)
				if (Array.isArray(storeAccountingItemsResponse.data.storeAmounts)) {
					accountingItemsForStore = storeAccountingItemsResponse.data.storeAmounts.map((item: any) => new StoreAccountingAmount(item));
				}
				if (Array.isArray(storeAccountingItemsResponse.data.globalAmounts)) {
					globalAccountingItems = storeAccountingItemsResponse.data.globalAmounts.map((item: any) => new GlobalAccountingAmount(item));
				}
			}

			populateCategoriesNew();
		} catch (err) {
			console.error(err);
			util.toastr('error', 'Getting Accounting Values', 'There was an unexpected error. Please refresh or contact support.');
		} finally {
			isBusyText.value = null;
		}
	}

	/**
	 * Loads the charter of accounts and journals
	 */
	async function loadCharterOfAccountsAndJournals() {
		try {
			isBusyText.value = 'Retrieving Accounting Data...';

			const coaAndJournalInfo = await AccountingHelper.loadCharterOfAccountsAndJournals(fimenu.store.storeCode);
			charterOfAccounts.value = coaAndJournalInfo.charterOfAccounts;
			journalInfo.value = coaAndJournalInfo.journalInfo;
		} catch (err) {
			console.error(err);
			util.toastr('error', 'Getting Accounting Values', 'There was an unexpected error. Please refresh or contact support.');
		} finally {
			isBusyText.value = null;
		}
	}

	/**
	 * Opens the posting modal
	 */
	function openPostingModal() {
		accountingHelper.openPostingModal(fimenu, selectedTransactionsForPosting.value, isPosting.value, async () => await init(true));
	}

	/**
	 * Opens the edit accounting modal
	 * @param {AccountingTransaction} transaction - The transaction to edit
	 */
	async function openEditAccountingModal(transaction: AccountingTransaction) {
		if (fimenu.isSpectator) return;

		const dealAccountingItem = dealAccountingItems.find(i => i.id == transaction.referenceId);
		const storeAccountingItem = accountingItemsForStore.find(i => i.propertyPath == transaction.referenceId);

		const masterControls = accountingHelper.getMasterControlsForStoreAccountingItem(fimenu.store, storeAccountingItem);

		//SET THE STORE ACCOUNTING AMOUNT TO THE DEAL ACCOUNTING ITEM AMOUNT
		storeAccountingItem.amount = dealAccountingItem.amount;

		// Implement the logic to open the modal for editing the accounting item
		$modal.open(modalEditAccountingItem, {
			name: 'modalEditAccountingItem',
			passedData: {
				amountItem: storeAccountingItem, //storeAccountingItem
				dealAccountingItem: dealAccountingItem, // dealAccountingItem
				masterControls: masterControls, // we need to know the control1, control2, description, etc
				journalInfo: journalInfo.value,
				charterOfAccounts: charterOfAccounts.value,
				isGloballyIgnored: globalAccountingItems.some(i => i.propertyPath == transaction.referenceId && i.isIgnored),
				fimenu: fimenu,
			},
			backdrop: false,
			postFunction: async (modifiedAccountingItem: StoreAccountingAmount) => {
				//REEVALUATE THE DEAL
				await init(true);
			},
		});
	}

	/**
	 * Loads the transactions
	 * @param {RichTableServerSide} transactionsPagedRequest - The transactions paged request
	 * @returns {Promise<RichTableServerSide>}
	 */
	async function loadTransactions(transactionsPagedRequest: RichTableServerSide) {
		transactionsPagedRequest.additionalObject = { dealId: fimenu.id };
		return await accountingAPI.getTransactionsPaged(transactionsPagedRequest);
	}

	/**
	 * Finalizes a deal without opening the posting modal
	 */
	async function finalizeDeal() {
		try {
			if (hasAccountingFinished.value) {
				isBusyText.value = 'Reversing this deal...';
				await fimenu.setDealStatus(DealStatuses.AccountingInProcess);
				fimenu.clearAccountingValues();
				await init();
			} else {
				isBusyText.value = 'Posting this deal...';

				const canProceed = await checkChecklist();
				if (!canProceed) return;

				await fimenu.setDealStatus(DealStatuses.Finalized);
			}
		} catch (err) {
			console.error(err);
			util.toastr('error', 'ERROR', err);
		} finally {
			isBusyText.value = null;
			selectedTransactionsForPosting.value = [];
		}
	}

	/**
	 * Checks if there's any outstanding items on the checklist that are tied to the Posting section. Returns `true` if there's no pending items.
	 * @returns {Promise<boolean>}
	 */
	async function checkChecklist(): Promise<boolean> {
		if (!ChecklistHelper.isDealChecklistEnabled(fimenu)) return true;

		return await ChecklistHelper.canContinueFromSection(
			fimenu,
			FIMenuSection.Posting,
			async () => {
				// This method will only ever get called if all checklist items get cleared from the modal.
				try {
					isBusyText.value = 'Posting this deal...';
					await fimenu.setDealStatus(DealStatuses.Finalized);
				} catch (err) {
					console.error(err);
					util.toastr('error', 'Error Posting Deal', (err as Error)?.message ?? 'An unknown error occurred.');
				} finally {
					isBusyText.value = null;
				}
			},
			'Finalize Deal',
		);
	}

	/**
	 * Populates the categories
	 */
	function populateCategoriesNew() {
		categories.value = accountingHelper.categoryEnums.enumList
			.filter(category => dealAccountingTransactions.value.some(i => i.category == category.value))
			.sort((a, b) => a.orderInList - b.orderInList)
			.map(category => ({ pageSize: DEFAULT_PAGE_SIZE, category: category }));
	}

	/**
	 * Returns all filtered transactions for a given category
	 * @param {ToListItems<AccountingValueCategory>} category - The category
	 * @returns {AccountingTransaction[]}
	 */
	function getItemsInCategory(category: ToListItems<AccountingValueCategory>): AccountingTransaction[] {
		let filteredItems: AccountingTransaction[] = null;

		//OUR SEARCH TEXT HAS MORE THAN 3 CHARACTERS, NOW WE CAN FILTER BY SEARCH TEXT
		if (hasSearchText.value) {
			//BUILD THE TEXT FILTER
			const searchTextLower = searchText.value.toLowerCase();
			let searchFilter = (item: AccountingTransaction) => {
				const nameMatch = formatPath(item.name).toLowerCase().indexOf(searchTextLower) > -1;
				const amountNumberMatch = `${item.amount}`.indexOf(searchTextLower) > -1;
				const amountPriceMatch = `${formatAmount(item.amount)}`.indexOf(searchTextLower) > -1;

				return nameMatch || amountNumberMatch || amountPriceMatch;
			};

			//APPLY TEXT FILTER
			filteredItems = dealAccountingTransactions.value.filter(searchFilter);
		} else {
			//NO TEXT FILTER APPLIED
			filteredItems = dealAccountingTransactions.value;
		}

		return filteredItems.filter(i => i.category == category.value);
	}

	/**
	 * Returns all transactions that are not ignored and are less than the page size for a given category
	 * @param {ICategoryTableRow} row - The category row
	 * @returns {AccountingTransaction[]}
	 */
	function validTransactionsForCategory(row: ICategoryTableRow): AccountingTransaction[] {
		return getItemsInCategory(row.category)
			.filter(c => !c.isIgnored && c.hasAmount())
			.filter((c, idx) => idx < row.pageSize);
	}

	/**
	 * Returns all transactions that are ignored for a given category
	 * @param {ToListItems<AccountingValueCategory>} category - The category
	 * @returns {AccountingTransaction[]}
	 */
	function ignoredTransactionsForCategory(category: ToListItems<AccountingValueCategory>): AccountingTransaction[] {
		return getItemsInCategory(category).filter(i => i.isIgnored);
	}

	/**
	 * Returns all transactions that have an amount of 0 or null for a given category
	 * @param {ToListItems<AccountingValueCategory>} category - The category
	 * @returns {AccountingTransaction[]}
	 */
	function zeroAmountTransactionsForCategory(category: ToListItems<AccountingValueCategory>): AccountingTransaction[] {
		return getItemsInCategory(category).filter(i => !i.isIgnored && !i.hasAmount());
	}

	/**
	 * Returns the number of unmapped items in a given category
	 * @param {ICategoryTableRow} row - The category row
	 * @returns {AccountingTransaction[]}
	 */
	function getUnmappedInCategory(row: ICategoryTableRow): AccountingTransaction[] {
		return getItemsInCategory(row.category).filter(i => {
			return !i.isIgnored && i.hasAmount() && !i.hasAccountNumber();
		});
	}

	/**
	 * Returns true if the category has unmapped items
	 * @param {ICategoryTableRow} row - The category row
	 * @returns {boolean}
	 */
	function hasUnmappedInCategory(row: ICategoryTableRow): boolean {
		return getUnmappedInCategory(row).length > 0;
	}

	/**
	 * Returns the total number of unmapped items in all categories
	 * @returns {number}
	 */
	function getTotalUnmapped(): number {
		let totalUnmapped = 0;
		//GET THE TOTAL NUMBER OF UNMAPPED ITEMS IN ALL CATEGORIES
		categories.value.forEach(row => {
			totalUnmapped += getUnmappedInCategory(row).length;
		});

		return totalUnmapped;
	}

	/**
	 * Returns true if the category has accounting items
	 * @param {ICategoryTableRow} row - The category row
	 * @returns {boolean}
	 */
	function hasValidTransactionsForCategory(row: ICategoryTableRow): boolean {
		return validTransactionsForCategory(row).length > 0;
	}

	/**
	 * Returns true if the category is the "Ignored" category
	 * @param {ToListItems<AccountingValueCategory>} category - The category
	 * @returns {boolean}
	 */
	function isIgnoredList(category: ToListItems<AccountingValueCategory>): boolean {
		return category.value == AccountingValueCategory.Ignored;
	}

	/**
	 * Maps the transactions with fake data
	 */
	function mapWithFakeData() {
		const transactionsToMap: AccountingTransaction[] = [];
		// only map the valid transactions. Use the categories list to get all the valid transactions for each category
		categories.value.forEach(row => {
			transactionsToMap.push(...getUnmappedInCategory(row));
		});

		transactionsToMap.forEach((item, index) => {
			item.company = 'COMPANY-AUTOFILLED';
			item.control1 = 'CONTROL1-AUTOFILLED';
			item.control2 = 'CONTROL2-AUTOFILLED';
			item.description = 'DESCRIPTION-AUTOFILLED';

			item.accountNumber = `${index + 1}-AUTOFILL`;
			item.accountType = getRandomEnumValue(DYWAccountType);
			item.profitFlag = getRandomEnumValue(ProfitFlags);
			item.transactionType = AccountingTransactionTypes.Post;
		});
		console.log(`${transactionsToMap.length} transactions mapped with fake data`);
	}

	/**
	 * Confirms the status change
	 */
	async function confirmStatusChange() {
		if (!selectedDealStatus.value) return;

		await fimenu.setDealStatus(selectedDealStatus.value);
	}

	/**
	 * Returns the filtered deal statuses
	 * @returns {DealStatus[]}
	 */
	function filteredDealStatuses() {
		return fimenu.dealStatusEnumsHelper.enumList.filter(
			status =>
				status.value >= DealStatuses.FiInProcess &&
				status.value <= DealStatuses.AccountingInProcess &&
				!fimenu.dealStatusEnumsHelper.isInAFinalizedStep(status.value) &&
				status.value !== fimenu.dealStatus,
		);
	}

	/**
	 * Formats the path
	 * @param {string} path - The path
	 * @returns {string}
	 */
	function formatPath(path: string): string {
		return util.formatForAccounting(path);
	}

	/**
	 * Formats the amount
	 * @param {number} amount - The amount
	 * @returns {string}
	 */
	function formatAmount(amount: number): string {
		return util.formatPrice(amount);
	}

	/**
	 * Shows more items for a category
	 * @param {ICategoryTableRow} row - The category row
	 * @param {IntersectionObserver} observer - The observer
	 */
	function showMoreItemsForCategory(row: ICategoryTableRow, observer: IntersectionObserver) {
		row.pageSize = row.pageSize + DEFAULT_PAGE_SIZE;

		//if we reach the end of the items for this category, stop watching for more items
		if (row.pageSize > getItemsInCategory(row.category).length && observer) {
			observer.disconnect();
		}
	}

	/**
	 * Resets all page sizes
	 */
	function resetAllPageSizes() {
		categories.value.forEach(cat => {
			cat.pageSize = DEFAULT_PAGE_SIZE;
		});
	}

	/**
	 * Goes to the next error for an accordion
	 * @param {ICategoryTableRow} row - The category row
	 */
	function goToTheNextErrorInTheCategory(row: ICategoryTableRow) {
		//find the index of the accordion
		const indexOfSelectedAccordion = categories.value.findIndex(i => i.category.value == row.category.value);

		// reset all page sizes
		categories.value.forEach((cat, index) => {
			if (index !== indexOfSelectedAccordion) {
				cat.pageSize = DEFAULT_PAGE_SIZE;
			} else {
				cat.pageSize = getItemsInCategory(cat.category).length + DEFAULT_PAGE_SIZE;
			}
		});

		//calculate the next error in this accordion
		const transactionWithError: AccountingTransaction = getItemsInCategory(row.category).find(item => !item.accountNumber);
		const errorItemElementId = getErrorElementId(transactionWithError);

		//go to that error
		setTimeout(() => {
			const toScrollTo = document.getElementById(errorItemElementId);
			toScrollTo?.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'nearest',
			});
		}, 500);
	}

	/**
	 * Returns the error element id
	 * @param {AccountingTransaction} item - The item
	 * @returns {string}
	 */
	function getErrorElementId(item: AccountingTransaction): string {
		return item.name.split(' ').join('-');
	}

	/**
	 * Returns the month and year
	 * @returns {string}
	 */
	function getMonthAndYear() {
		const postedDate = moment(fimenu.accountingInfo.accountingMonth);
		return `${postedDate.format('MMMM YYYY')}`;
	}

	/**
	 * Handles the search query
	 */
	function handleSearchQuery() {
		if (hasSearchText.value) {
			showOtherAmounts.value = true;
		} else {
			showOtherAmounts.value = false;

			//close all accordions in the accordion list
			accordionListRef.value.closeAll();
		}
	}

	/**
	 * Handles the transaction selection change
	 * @param {AccountingTransaction[]} selectedData - The selected data
	 */
	function handleTransactionSelectionChange(selectedData: AccountingTransaction[]) {
		selectedTransactionsForPosting.value = selectedData;
	}

	//#endregion
</script>
<style>
	.PanelAccounting {
		height: 100%;
		box-sizing: border-box;
		display: flex;
		position: relative;
		overflow: hidden;
		justify-content: flex-start;
		max-height: 655px;
		gap: 10px;
		--accounting-container-height: 90%;
	}

	.PanelAccounting h3,
	.PanelAccounting h4 {
		margin: 0;
	}

	.PanelAccounting .panel {
		height: 80%;
	}

	.PanelAccounting .button-container {
		height: 50px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
	}

	.PanelAccounting .accounting-container {
		overflow-y: auto;
		overflow-x: hidden;
	}

	.PanelAccounting .loading-saturn-anim.fade-in.IsBusySectionComponent-container {
		height: 100%;
		box-sizing: border-box;
	}

	.PanelAccounting .category-details {
		transition: var(--transition);
	}

	.PanelAccounting .warning-container {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		color: var(--error-color);
		font-weight: 600;
		margin: 10px 0 15px 0;
		gap: 5px;
	}

	.PanelAccounting table.rich-table {
		border: unset;
	}

	.PanelAccounting table.rich-table thead {
		background: var(--background-color);
	}

	.PanelAccounting table.rich-table thead th {
		border-bottom: 2px solid var(--main-color);
	}

	.PanelAccounting table.rich-table .rich-table-header.clickable:hover {
		background: var(--black-10percent);
		box-shadow: unset;
	}

	.PanelAccounting table.rich-table .rich-table-header:hover .rich-header .rich-header-options .rich-header-sort {
		color: var(--disabled-color);
	}

	.PanelAccounting table.rich-table .rich-table-header:hover .rich-header .rich-header-options .rich-header-sort div.active {
		color: var(--main-color);
	}

	.PanelAccounting .rich-table-container {
		width: 100%;
		font-size: 0.8rem;
		height: 100%;
		overflow: auto;
	}

	.PanelAccounting .accordion-header .button-unwind {
		padding: 0 10px;
	}

	.PanelAccounting .container-header {
		box-sizing: border-box;
		border-bottom: 1px solid var(--main-color);
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 15px;
	}

	.PanelAccounting .container-header h3 {
		color: var(--main-color);
		font-weight: 600;
		font-size: 1.25rem;
	}

	.PanelAccounting .container-header .subtitle {
		color: var(--third-color);
		font-weight: 800;
		font-size: 0.75rem;
	}

	.PanelAccounting .container-header {
		width: 100%;
	}

	.PanelAccounting .accordion-list {
		width: 100%;
		place-self: center;
	}

	.PanelAccounting .container-header button {
		width: auto !important;
	}

	.PanelAccounting .container-header button.primary {
		background: var(--main-color) !important;
	}

	.PanelAccounting .feedback,
	.PanelAccounting .hidden-items-container {
		display: flex;
		justify-content: center;
		align-items: center;
		color: var(--text-color);
		overflow: hidden;
		transition: var(--transition);
		margin-top: 25px;
	}

	.PanelAccounting .feedback {
		margin-top: unset;
		height: 0px;
		opacity: 0;
		font-size: 1.05rem;
		font-weight: 500;
		position: absolute;
		bottom: 10%;
		width: 100%;
		box-sizing: border-box;
		z-index: 111;
	}

	.PanelAccounting .hidden-items-container {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}

	.PanelAccounting .feedback.expanded {
		opacity: 1;
	}

	.PanelAccounting .feedback.expanded {
		height: 45px;
		color: var(--error-color);
		overflow-x: hidden;
	}

	.PanelAccounting .feedback.errors-found {
		color: var(--error-color);
		pointer-events: none;
	}

	.PanelAccounting .accounting-container {
		height: var(--accounting-container-height);
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		overflow-y: scroll;
		overflow-x: hidden;
	}

	.PanelAccounting .blink {
		animation: blinking 1.2s infinite;
	}

	.PanelAccounting .no-items-container {
		height: 100px;
		display: flex;
		justify-content: center;
		align-items: center;
		font-weight: 500;
		font-size: 1.1rem;
		width: 100%;
	}

	.PanelAccounting .content-helper-container {
		width: 100%;
		padding: 10px;
		box-sizing: border-box;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.PanelAccounting .content-helper-container input {
		width: 50%;
	}

	.PanelAccounting h3.zero-amounts {
		margin: 0;
		margin-bottom: 5px;
	}

	.PanelAccounting .smaller-cell:not(.rich-table-header) {
		max-width: 40px;
		word-break: break-word;
	}

	.PanelAccounting .name-cell {
		width: 200px;
	}

	.PanelAccounting .name-cell-content {
		cursor: pointer;
		transition: var(--transition);
	}

	.PanelAccounting .name-cell-content:hover {
		color: var(--main-color);
	}

	.PanelAccounting .Accordion .children-container {
		margin: 10px 0;
	}

	.PanelAccounting .account-number {
		display: flex;
		justify-content: center;
	}

	.PanelAccounting .account-number i {
		color: var(--error-color);
		animation: throb 1.2s infinite;
		scale: 1.3;
	}

	.PanelAccounting .account-number.clickable {
		cursor: pointer;
	}

	.PanelAccounting .account-number.clickable:hover i {
		color: var(--main-color);
	}

	.PanelAccounting .header-buttons {
		max-width: 40%;
		display: flex;
		justify-content: flex-end;
		gap: 10px;
	}

	.PanelAccounting .header-buttons buttons {
		white-space: nowrap;
	}

	.PanelAccounting .rich-table tbody tr {
		cursor: pointer;
		transition: var(--transition);
	}

	.PanelAccounting .rich-table tbody tr:hover {
		background-color: var(--black-10percent);
	}

	.PanelAccounting .posting-btn {
		width: 15%;
	}

	.PanelAccounting .panel .panel-header .grid-title {
	}
</style>
